import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Award, Users, Calendar, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Helmet } from 'react-helmet-async';

const AboutPage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { label: 'Års erfaring', value: '10+', icon: Award },
    { label: 'Personlig service', value: '100%', icon: Heart },
    { label: 'Fleksibilitet', value: 'Høj', icon: Sparkles },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Om os | Golf4Life - Personlige golfrejser til Spanien</title>
        <meta name="description" content="Mød ægteparret bag Golf4Life. Vi er pensionister med over 10 års erfaring i at arrangere personlige og fleksible golfrejser til Cádiz, Spanien." />
      </Helmet>

      <Navbar solid={true} />

      <main className="pt-20 md:pt-24">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative z-10 pt-16 md:pt-28 pb-16 md:pb-24 lg:pb-32 lg:w-1/2">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-xl"
              >
                <span className="inline-block text-slate-800 text-sm font-semibold uppercase tracking-wider mb-6">
                  Mød værterne
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-slate-900 leading-tight mb-6">
                  Vi skaber rammerne for dit <span className="text-[var(--brand)]">perfekte</span> golfophold
                </h1>
                <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8">
                  Golf4Life er ikke et stort rejsebureau med hundredevis af ansatte. Vi er et dansk ægtepar, der selv elsker golf og Spanien, og som har gjort det til vores passion at dele det med andre.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link 
                    to="/kontakt" 
                    className="inline-flex items-center justify-center px-8 py-4 rounded-full border-2 border-slate-900 bg-transparent text-slate-900 font-semibold text-lg hover:-translate-y-1 transition-all shadow-lg group"
                  >
                    Book dit ophold
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <a 
                    href="/kontakt" 
                    className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-[var(--brand)] text-white font-semibold text-lg hover:-translate-y-1 transition-all shadow-lg"
                  >
                    Kontakt os i dag
                  </a>
                </div>
              </motion.div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2 relative h-[400px] lg:h-full"
          >
            <img 
              src="https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/7eba72c7-1f45-4dbe-b026-ae1ff4b774ee.jpg" 
              alt="Ægteparret bag Golf4Life" 
              className="absolute inset-0 w-full h-full object-cover"
            />
            
            <div className="absolute bottom-12 left-12 z-20 bg-white p-6 rounded-2xl shadow-xl border border-slate-100 max-w-[240px]">
              <div className="flex items-center gap-3 mb-2">
                <div className="text-[var(--brand)] flex items-center justify-center">
                  <Heart size={24} />
                </div>
                <span className="font-semibold text-slate-900">Personligt</span>
              </div>
              <p className="text-sm text-slate-600 leading-snug">
                Vi er selv til stede i Spanien og hjælper dig med alt det praktiske under dit ophold.
              </p>
            </div>
          </motion.div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-white border-y border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center text-[var(--brand)] mb-4">
                    <stat.icon size={32} />
                  </div>
                  <div className="text-3xl font-semibold text-slate-900 mb-1">{stat.value}</div>
                  <div className="text-sm font-medium text-slate-500 uppercase tracking-wider">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Philosophy Section */}
        <section className="py-20 bg-[var(--brand)] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-semibold mb-8">Vores filosofi</h2>
                <div className="space-y-8">
                  <div className="flex gap-6">
                    <div className="flex items-center justify-center shrink-0">
                      <Sparkles className="text-white/80" size={32} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Høj fleksibilitet</h3>
                      <p className="text-white/70 leading-relaxed">
                        Vi ved, at ingen gæster er ens. Derfor indretter vi os efter dig. Uanset om du vil spille golf hver dag eller kombinere det med kulturelle oplevelser, finder vi en løsning.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-6">
                    <div className="flex items-center justify-center shrink-0">
                      <Calendar className="text-white/80" size={32} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">De rigtige sæsoner</h3>
                      <p className="text-white/70 leading-relaxed">
                        Vi fokuserer på de måneder, hvor klimaet i Spanien er mest behageligt for golfspillere: oktober til december og januar til marts. Her undgår man den værste sommervarme og den danske vinterkulde.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-6">
                    <div className="flex items-center justify-center shrink-0">
                      <Heart className="text-white/80" size={32} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Tryghed og fællesskab</h3>
                      <p className="text-white/70 leading-relaxed">
                        Vi er selv til stede i området og sørger for, at alt klapper. Vi arrangerer også fælles middage og sociale aktiviteter for dem, der har lyst til at lære de andre gæster at kende.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="grid grid-cols-2 gap-4">
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl"
                  >
                    <img 
                      src="https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/397666f3-e4fa-4a9c-bd59-49645054de02.jpg" 
                      alt="Golfbane i Spanien" 
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl mt-12"
                  >
                    <img 
                      src="https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/6e6fc98f-d996-4619-a2df-7c8102a25a39.png" 
                      alt="La Barrosa strand" 
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                </div>
                <div className="absolute bottom-6 -left-6 bg-white text-[var(--brand)] p-6 rounded-2xl shadow-xl hidden md:block z-10">
                  <div className="text-sm font-semibold uppercase tracking-wider">Glade gæster</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 mb-8">Skal vi planlægge din næste golfrejse?</h2>
            <p className="text-xl text-slate-600 mb-12 max-w-2xl mx-auto leading-relaxed">
              Tag fat i os til en uforpligtende snak om dine ønsker. Vi glæder os til at høre fra dig og byde dig welkommen i Spanien.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/kontakt" 
                className="inline-flex items-center justify-center px-10 py-5 rounded-full bg-[var(--brand)] text-white font-semibold text-xl hover:-translate-y-1 transition-all shadow-xl"
              >
                Kontakt os nu
                <ArrowRight className="ml-2" size={24} />
              </a>
              <a 
                href="tel:22467722" 
                className="inline-flex items-center justify-center px-10 py-5 rounded-full border-2 border-slate-200 text-slate-900 font-semibold text-xl hover:-translate-y-1 transition-all"
              >
                Ring til os
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
